// May 25, 2026 — Dark mode helper.
//
// CSS-filter-based dark mode (see index.css `html[data-dark="1"]`).
// State is persisted to localStorage for instant application on page
// load, AND mirrored to the server so the user gets the same setting
// across devices. Server is authoritative on next login; localStorage
// wins for "right now" so the page never flashes the wrong palette.

import { useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";

const LS_KEY = "mysilo_dark_mode";

export function applyDarkMode(enabled) {
  const html = document.documentElement;
  if (enabled) html.setAttribute("data-dark", "1");
  else html.removeAttribute("data-dark");
}

export function readLocalDarkMode() {
  try {
    return window.localStorage.getItem(LS_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeLocalDarkMode(enabled) {
  try {
    window.localStorage.setItem(LS_KEY, enabled ? "1" : "0");
  } catch {
    // ignore storage failures (private browsing)
  }
}

// Hook for the toggle UI. Loads the current state on mount, lets the
// caller flip it, and pushes the change to the server.
export function useDarkMode() {
  const [enabled, setEnabled] = useState(readLocalDarkMode());
  const [saving, setSaving] = useState(false);

  // First mount: pull the server's saved state to override local (only
  // if it differs). Server is the source of truth across devices.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!getToken()) return;  // anon viewers use local-only dark mode (no server sync)
        const r = await api.get("/me/dark-mode");
        if (cancelled) return;
        const remote = !!r.data?.enabled;
        if (remote !== enabled) {
          setEnabled(remote);
          writeLocalDarkMode(remote);
          applyDarkMode(remote);
        }
      } catch {
        // No-op — anonymous viewers + offline mode just use local.
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    writeLocalDarkMode(next);
    applyDarkMode(next);
    setSaving(true);
    try {
      await api.patch("/me/dark-mode", { enabled: next });
    } catch {
      // Silent — local state already applied. Server will catch up on
      // next attempt or next page load.
    } finally {
      setSaving(false);
    }
  };

  return { enabled, toggle, saving };
}
